import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Linkedin, Instagram, Twitter, Facebook, MapPin } from "lucide-react";
import { profile, stats } from "../../mock/mock";

const socialIcon = (label) => ({ LinkedIn: Linkedin, Instagram, Twitter, Facebook }[label] || Linkedin);

export default function Hero() {

  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
      <div className="halo -top-40 -left-40" />
      <div className="halo bottom-0 right-[-200px] opacity-70" />
      <div className="absolute inset-0 pointer-events-none opacity-[0.08] [background:linear-gradient(rgba(255,255,255,0.4)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.4)_1px,transparent_1px)] [background-size:60px_60px]" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
          className="lg:col-span-7">
          <h1 className="font-display text-[11vw] md:text-[80px] lg:text-[100px] leading-[0.95] tracking-[-0.03em]">
            <span className="block text-[var(--text)] text-[5vw] md:text-[38px] lg:text-[44px] font-light italic mb-3 tracking-[-0.01em]">I'm</span>
            <span className="block text-[var(--text)]">Anand Baral,</span>
            <span className="block italic text-[var(--sage)]">Digital Marketer.</span>
          </h1>

          <p className="mt-8 max-w-xl text-[18px] md:text-[20px] leading-[1.6] text-[var(--text-dim)] font-light">
            I can scale your revenue without increasing your current marketing budget.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#work" className="btn-primary">View Selected Work <ArrowUpRight size={18} /></a>
            <a href="#contact" className="btn-ghost">Start a Project</a>
          </div>

          <div className="mt-12 flex items-center gap-5">
            <span className="mono text-[11px] tracking-[0.22em] uppercase text-[var(--muted)]">Follow</span>
            <div className="h-px flex-1 max-w-[60px] bg-white/10" />
            <div className="flex items-center gap-4">
              {profile.socials.map((s) => {
                const Icon = socialIcon(s.label);
                return (
                  <a key={s.label} href={s.url} target="_blank" rel="noreferrer" aria-label={s.label}
                    className="h-10 w-10 rounded-full border border-white/10 inline-flex items-center justify-center text-[var(--text-dim)] hover:text-[var(--sage)] hover:border-[var(--sage)]/50 transition-colors">
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.2, 0.7, 0.2, 1] }}
          className="lg:col-span-5 relative">
          <div className="relative mx-auto aspect-[4/5] w-full max-w-[460px]">
            <div className="absolute inset-0 rounded-[28px] glass-strong overflow-hidden">
              <div className="absolute -inset-1 rounded-[32px] ring-1 ring-[var(--sage)]/15" />
              <img src={profile.portraitUrl} alt="Anand Baral"
                className="h-full w-full object-cover object-top"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            </div>

            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }}
              className="absolute -left-2 sm:-left-6 top-2 lg:top-10 glass rounded-xl sm:rounded-2xl px-2 py-2 sm:px-4 sm:py-3 w-[120px] sm:w-[168px]">
              <div className="mono text-[8px] sm:text-[10px] tracking-[0.2em] uppercase text-[var(--sage)]">Worked With</div>
              <div className="font-display text-lg sm:text-2xl mt-1">40+ Brands</div>
              <div className="text-[10px] sm:text-[11px] text-[var(--text-dim)]">Across industries</div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.6 }}
              className="absolute -right-2 sm:-right-4 bottom-4 lg:bottom-12 glass rounded-2xl px-4 py-3 w-[180px]">
              <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--sage)]">Based In</div>
              <div className="mt-1 flex items-center gap-2 font-medium">
                <MapPin size={14} className="text-[var(--sage)]" /> Jaipur, IN
              </div>
              <div className="text-[11px] text-[var(--text-dim)]">Working worldwide</div>
            </motion.div>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-3">
            {stats
              .filter((s) => s.label !== "Brands Served" && s.label !== "Client's Rating")
              .map((s) => (
                <div key={s.label} className="text-center">
                  <div className="font-display text-2xl md:text-3xl text-[var(--text)]">{s.value}</div>
                  <div className="mono text-[10px] tracking-[0.15em] uppercase text-[var(--muted)] mt-1">{s.label}</div>
                </div>
              ))}
          </div>
        </motion.div>
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 mt-16">
        <div className="hairline w-full" />
        <div className="mt-4 flex items-center justify-center mono text-[10px] tracking-[0.22em] uppercase text-[var(--muted)]">
          <span>Scroll to explore</span>
        </div>
      </div>
    </section>
  );
}
